<template>
  <div :style="styleObject">
    <button
      style="display: none"
      type="button"
      class="btn-show-modal-confirm-passcode"
      data-bs-toggle="modal"
      data-bs-target="#modalConfirmPasscode"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-hide-modal-confirm-passcode"
      data-bs-dismiss="modal"
      data-bs-target="#modalConfirmPasscode"
    ></button>
    <div
      class="modal fade"
      id="modalConfirmPasscode"
      tabindex="-1"
      aria-labelledby="modalConfirmPasscodeLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <KskModalShell :width="760" :show-close="false">
        <div class="ksk-passcode">
          <div class="ksk-passcode__icon">
            <span class="material-icons-round">lock</span>
          </div>
          <h2 id="modalConfirmPasscodeLabel" class="ksk-passcode__title">{{ $t("kiosk.passcode.title") }}</h2>
          <p class="ksk-passcode__caption">{{ $t("kiosk.passcode.caption") }}</p>

          <div class="ksk-passcode__display" @click="focusInput">
            <!-- Hidden input keeps physical-keyboard entry + Enter to submit. -->
            <input
              ref="passcodeInput"
              type="password"
              class="ksk-passcode__hidden"
              v-model="passcode"
              inputmode="numeric"
              maxlength="4"
              autocomplete="off"
              @input="onInput"
              @keyup.enter="submit"
            />
            <div
              v-for="i in passcodeLength"
              :key="i"
              class="ksk-passcode__box"
              :class="{
                'ksk-passcode__box--active': passcode.length + 1 === i,
                'ksk-passcode__box--error': !status,
              }"
            >
              <span v-if="passcode[i - 1]" class="ksk-passcode__dot"></span>
            </div>
          </div>

          <!-- Reserved error line: always takes its height so the keypad never jumps. -->
          <div class="ksk-passcode__error" role="alert">
            <template v-if="!status">
              <span class="material-icons-round">error_outline</span>
              <span>{{ $t("kiosk.passcode.incorrect") }}</span>
            </template>
          </div>

          <KskNumericKeypad class="ksk-passcode__keypad" clear-token="C" back-token="X" @press="onKeyPress" />
        </div>

        <template #footer>
          <KskButton variant="outlined" class="ksk-passcode__btn" data-bs-dismiss="modal">{{ $t("kiosk.common.cancel") }}</KskButton>
          <KskButton
            class="ksk-passcode__btn"
            :disabled="passcode.length < passcodeLength || loaderActive"
            @click="submit"
          >{{ $t("kiosk.common.confirm") }}</KskButton>
        </template>
      </KskModalShell>
      <material-loading :active="loaderActive" />
    </div>
  </div>
</template>

<script>
import KioskService from "../../../services/kiosk.service";
import MaterialLoading from "@/components/MaterialLoading.vue";
import KskModalShell from "./ui/KskModalShell.vue";
import KskNumericKeypad from "./ui/KskNumericKeypad.vue";
import KskButton from "./ui/KskButton.vue";
import { localStorageName } from "@/config/utils";

export default {
  name: "ModalConfirmPasscode",
  components: {
    MaterialLoading,
    KskModalShell,
    KskNumericKeypad,
    KskButton,
  },
  props: {
    keyReload: {
      required: true,
    },
  },
  data() {
    return {
      merchantCode: "",
      branchCode: "",
      passcode: "",
      searchTimerId: null,
      status: true,
      loaderActive: false,
      memberInfo: {},
      // A member passcode is always 4 digits (crm.api MemberOtpRequest).
      passcodeLength: 4,
    };
  },
  mounted() {
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
  },
  computed: {
    styleObject() {
      return {
        "--color": this.$store.state.color,
      };
    },
  },
  methods: {
    confirmPasscode() {
      this.showLoader();
      this.status = true;
      let dataForm = {
        branch_code: this.branchCode,
        phone_number: this.memberInfo.phone_number ?? '',
        passcode: this.passcode,
      };

      KioskService.confirmPasscode(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.status = response.data.data.status;

            if (this.status) {
              this.$emit("confirm-passcode");
            }
          }
          this.hideLoader();
        },
        (error) => {
          console.log(error.response.data.result.message);
          this.hideLoader();
        }
      );
    },
    // KSK031 keypad / hidden-input helpers — they only edit `passcode`;
    // submitting still goes through confirmPasscode().
    onKeyPress(key) {
      if (key === "C") {
        this.passcode = "";
      } else if (key === "X") {
        this.passcode = this.passcode.slice(0, -1);
      } else if (this.passcode.length < this.passcodeLength) {
        this.passcode += key;
      }
      this.status = true;
    },
    onInput() {
      this.passcode = String(this.passcode || "").replace(/\D/g, "").slice(0, this.passcodeLength);
      this.status = true;
    },
    focusInput() {
      if (this.$refs.passcodeInput) this.$refs.passcodeInput.focus();
    },
    submit() {
      if (this.passcode.length < this.passcodeLength || this.loaderActive) return;
      this.confirmPasscode();
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
  },
  watch: {
    keyReload() {
      this.passcode = "";
      this.status = true;
      this.memberInfo = JSON.parse(localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO")));
    },
  },
};
</script>

<style scoped>
/* KSK031 — P14 voucher passcode (theme 2). */
.modal {
  overflow-y: hidden !important;
}

.ksk-passcode {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.ksk-passcode__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-passcode__icon .material-icons-round {
  font-size: 48px;
}
.ksk-passcode__title {
  margin: var(--ksk-space-3) 0 0;
  font-family: var(--ksk-font);
  font-size: var(--ksk-headline-size);
  line-height: var(--ksk-headline-lh);
  font-weight: 700;
  color: var(--ksk-text);
}
.ksk-passcode__caption {
  margin: var(--ksk-space-1) 0 0;
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  color: var(--ksk-text-secondary);
}

.ksk-passcode__display {
  position: relative;
  display: flex;
  justify-content: center;
  gap: var(--ksk-space-2);
  margin-top: var(--ksk-space-4);
}
.ksk-passcode__hidden {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: none;
  caret-color: transparent;
}
.ksk-passcode__box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 112px;
  border: 2px solid transparent;
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface-muted);
  pointer-events: none;
}
.ksk-passcode__box--active {
  border-color: var(--ksk-primary);
  background: var(--ksk-surface);
}
.ksk-passcode__box--error {
  border-color: var(--ksk-error);
}
.ksk-passcode__dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--ksk-text);
}

.ksk-passcode__error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-1);
  min-height: var(--ksk-body-lh);
  margin: var(--ksk-space-2) 0;
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  color: var(--ksk-error);
}
.ksk-passcode__error .material-icons-round {
  font-size: 28px;
}
.ksk-passcode__keypad {
  width: 440px;
}

/* The shell's footer rule is scoped to the shell, so size the slotted buttons here. */
.ksk-passcode__btn {
  flex: 1 1 0;
  min-width: 0;
}
</style>
